// src/redux/reducers/index.ts
import {combineReducers} from 'redux';
import {persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {createTransform} from 'redux-persist';

import {RESET_USER} from '../actions/types';

import availability, {availabilityState} from './availability';
import review, {ReviewState} from './review';
import fullReport, {ReportState} from './fullReport';
import user, {UserState} from './user';
import student, {StudentState} from './student';
import {classReducer, ClassState} from './class'; // <— your new slice

// Transform to strip/rehydrate only the keys we need in fullReport
const ReportTransform = createTransform(
  (inbound: ReportState) => ({
    reportsByStatisticId: inbound.reportsByStatisticId || {},
    fullReport: inbound.fullReport || [],
  }),
  (outbound: any) => ({
    reportsByStatisticId: outbound?.reportsByStatisticId || {},
    fullReport: outbound?.fullReport || [],
  }),
  {whitelist: ['fullReport']},
);

// Persist configs per-slice
const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['user', 'isLoggedIn', 'partnerUser', 'teacher'],
  blacklist: [
    'isLoadingRequest',
    'error',
    'conversations',
    'subscribedConversationIds',
  ],
};

const availabilityPersistConfig = {
  key: 'availability',
  storage,
  whitelist: [], // nothing persisted
};

const reviewPersistConfig = {
  key: 'review',
  storage,
  whitelist: [], // nothing persisted
};

const fullReportPersistConfig = {
  key: 'fullReport',
  storage,
  whitelist: ['fullReport', 'reportsByStatisticId'], // Only persist these fields
  blacklist: ['selectedClassMonthlyReportArr', 'classMonthlyReportsCache'], // Explicitly exclude these (session-only)
  transforms: [ReportTransform],
};

const studentPersistConfig = {
  key: 'student',
  storage,
  whitelist: [], // nothing persisted
};

// You can choose to persist classState or not; here we do persist its selectedClassId
const classPersistConfig = {
  key: 'classState',
  storage,
  whitelist: ['selectedClassId'],
};

// Combine your slices, wrapping each one in its persistReducer
const appReducer = combineReducers({
  user: persistReducer(userPersistConfig, user),
  availability: persistReducer(availabilityPersistConfig, availability),
  review: persistReducer(reviewPersistConfig, review),
  student: persistReducer(studentPersistConfig, student),
  fullReport: persistReducer(fullReportPersistConfig, fullReport),
  classState: persistReducer(classPersistConfig, classReducer), // <— added
});

// Root reducer that can reset user+fullReport on logout
const rootReducer = (state: any, action: any) => {
  if (action.type === RESET_USER) {
    // purge these persisted slices
    storage.removeItem('persist:user');
    storage.removeItem('persist:fullReport');
    // (optionally remove 'persist:classState' if you want to clear selection on logout)
    // storage.removeItem('persist:classState');
    return appReducer(undefined, action);
  }

  // Migration: Clear old persisted monthly report caches on app load
  if (action.type === 'persist/REHYDRATE' && action.key === 'fullReport') {
    if (state?.fullReport?.selectedClassMonthlyReportArr || state?.fullReport?.classMonthlyReportsCache) {
      // Clear the session-only caches if they were incorrectly persisted
      return appReducer({
        ...state,
        fullReport: {
          ...state.fullReport,
          selectedClassMonthlyReportArr: [],
          classMonthlyReportsCache: [],
        }
      }, action);
    }
  }

  return appReducer(state, action);
};

export type reducersState = {
  user: UserState;
  availability: availabilityState;
  review: ReviewState;
  fullReport: ReportState;
  student: StudentState;
  classState: ClassState;
};

export default rootReducer;
